'use client';

import React, { useEffect, type ReactNode } from 'react';
import { useColors } from '../theme/ThemeProvider';
import { radius, space, zIndex } from '../theme/tokens';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Max modal width in px. Defaults to 480. */
  maxWidth?: number;
}

/**
 * Bottom-sheet modal with backdrop.
 * Closes on backdrop click or Escape.
 */
export function Modal({ open, onClose, children, maxWidth = 480 }: ModalProps) {
  const colors = useColors();

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: zIndex.modal,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: colors.white,
          borderRadius: `${radius.xl}px ${radius.xl}px 0 0`,
          padding: `${space[6]}px ${space[5]}px calc(env(safe-area-inset-bottom, 0px) + ${space[8]}px)`,
          width: '100%',
          maxWidth,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            width: 40,
            height: 4,
            borderRadius: 2,
            background: colors.border,
            margin: `0 auto ${space[5]}px`,
          }}
          aria-hidden
        />
        {children}
      </div>
    </div>
  );
}
